import { lazy, Suspense, useState } from "react";
import { useNavigate } from "react-router";
import { useLogout, useMe, useSelectChild } from "../api/hooks.ts";
import { Bee } from "../art/Bee.tsx";
import { Button, ChildAvatar, Icon } from "../components/ui.tsx";
import { copy } from "../copy/index.ts";
import type { ChildSummary } from "../api/types.ts";
import styles from "./Entry.module.css";

// the picture PIN pad only loads for a child who has a PIN (T3.4b)
const PinGate = lazy(() => import("./PinGate.tsx"));

/** C3 – "Ai đang học nhỉ?"; a child with a picture PIN picks it first (SPEC 2.3). */
export function ChooseChildScreen() {
  const me = useMe();
  const select = useSelectChild();
  const logout = useLogout();
  const navigate = useNavigate();
  const kids = me.data?.children ?? [];
  const [asking, setAsking] = useState<ChildSummary | null>(null);
  const open = (k: ChildSummary) =>
    k.hasPin ? setAsking(k) : select.mutate(k.id, { onSuccess: () => void navigate("/ban-do") });

  return (
    <div className={styles.page}>
      <div className={styles.whoHeader}>
        <Bee expression="wave" size={120} />
        <h1 className={styles.title}>{copy.chooseChild.title}</h1>
      </div>
      {kids.length === 0 && <p className={styles.subtitle}>{copy.chooseChild.empty}</p>}
      <div className={styles.kids}>
        {kids.map((k) => (
          <button
            key={k.id}
            type="button"
            className={styles.kid}
            disabled={select.isPending}
            onClick={() => open(k)}
          >
            <ChildAvatar avatar={k.avatar} outfit={k.outfit ?? {}} size={120} />
            <strong>{k.displayName}</strong>
            <span className={styles.kidMeta}>
              {copy.chooseChild.grade(k.grade ?? 2)} · {copy.chooseChild.level(k.level)} ·{" "}
              <Icon name="star" size={18} /> {k.starsTotal}
            </span>
          </button>
        ))}
      </div>
      {asking && (
        <Suspense fallback={null}>
          <PinGate
            child={asking}
            onOpened={() => void navigate("/ban-do")}
            onClose={() => setAsking(null)}
          />
        </Suspense>
      )}
      {select.isError && !asking && (
        <p className={styles.error} role="alert">
          {select.error.message}
        </p>
      )}
      <Button variant="secondary" size="s" onClick={() => void navigate("/phu-huynh")}>
        <Icon name="family" size={24} /> {copy.parent.entry}
      </Button>
      <Button
        variant="ghost"
        size="s"
        onClick={() => logout.mutate(undefined, { onSettled: () => void navigate("/") })}
      >
        {copy.chooseChild.logout}
      </Button>
    </div>
  );
}
